import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

const CACHE_KEY_ACTIVE = 'estimates_active'
const CACHE_KEY_ARCHIVED = 'estimates_archived'

/**
 * Serialize live line objects → compact storage format.
 * Standard catalog items: { item_id, qty, price }
 * Custom / ad-hoc items:  { _custom: true, name, cat, unit, qty, price, cost }
 */
export function serializeLines(lines) {
    return (lines || []).map(line => {
        if (line.is_custom || !line.id) {
            return {
                _custom: true,
                name:    line.name  || '',
                cat:     line.cat   || '',
                unit:    line.unit  || '',
                qty:     line.qty   ?? 1,
                price:   line.price ?? 0,
                cost:    line.cost  ?? 0,
            }
        }
        return {
            item_id: line.id,
            qty:     line.qty   ?? 1,
            price:   line.price ?? 0,
        }
    })
}

/**
 * Resolve stored compact lines → full live line objects.
 * Catalog items are looked up by item_id; name/unit/cat/inc/exc come from the
 * live catalog so any editor changes propagate automatically.
 * Only qty + price (quoted price) are preserved from the stored record.
 *
 * @param {Array}  storedLines  — serialized lines from the DB
 * @param {Object} catalogMap   — { [item.id]: catalogItem } from useCatalogItems
 */
export function resolveLines(storedLines, catalogMap) {
    return (storedLines || []).map(stored => {
        if (stored._custom) {
            return {
                id: null, is_custom: true,
                name: stored.name, cat: stored.cat, unit: stored.unit,
                qty: stored.qty, price: stored.price, cost: stored.cost,
                inc: [], exc: [], inc_ids: [], exc_ids: [],
            }
        }
        const cat = catalogMap?.[stored.item_id]
        if (!cat) {
            return {
                id: stored.item_id,
                name:  `[Removed: ${stored.item_id}]`,
                cat: '—', unit: 'Unit',
                qty: stored.qty, price: stored.price, cost: 0,
                inc: [], exc: [], inc_ids: [], exc_ids: [],
            }
        }
        return { ...cat, qty: stored.qty, price: stored.price }
    })
}

/**
 * useEstimates — fetch all non-archived estimates ordered by updated_at DESC.
 */
export function useEstimates() {
    const [estimates, setEstimates] = useState([])
    const [loading,   setLoading]   = useState(true)
    const [error,     setError]     = useState(null)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)

        const cached = await loadReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => null)
        if (cached) {
            setEstimates(cached.data)
            if (!silent) setLoading(false)
        }

        const { data, error: err } = await supabase
            .from('estimates')
            .select('*')
            .neq('status', 'archived')
            .order('updated_at', { ascending: false })

        if (id !== loadIdRef.current) return

        if (err) {
            setError(err.message)
            if (!cached) setEstimates([])
        } else {
            setEstimates(data || [])
            await saveReferenceCache(CACHE_KEY_ACTIVE, data || [], null).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    return { estimates, loading, error, reload: load, silentReload }
}

/**
 * useArchivedEstimates — fetch all archived estimates with archiver info.
 */
export function useArchivedEstimates() {
    const [estimates, setEstimates] = useState([])
    const [loading, setLoading]   = useState(true)
    const [error,   setError]     = useState(null)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)

        const cached = await loadReferenceCache(CACHE_KEY_ARCHIVED, null).catch(() => null)
        if (cached) {
            setEstimates(cached.data)
            if (!silent) setLoading(false)
        }

        const { data, error: err } = await supabase
            .from('estimates')
            .select('*')
            .eq('status', 'archived')
            .order('updated_at', { ascending: false })

        if (id !== loadIdRef.current) return

        if (err) {
            setError(err.message)
            if (!cached) setEstimates([])
        } else {
            setEstimates(data || [])
            await saveReferenceCache(CACHE_KEY_ARCHIVED, data || [], null).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    return { estimates, loading, error, reload: load, silentReload }
}

/**
 * Save (insert if new, update if estimate.id is set) an estimate.
 * Computes and stores a total snapshot for list display.
 * Returns the saved DB record.
 */
export async function saveEstimate(estimate, userId) {
    const total = (estimate.lines || []).reduce(
        (s, l) => s + (l.qty || 0) * (l.price || 0), 0
    )
    const payload = {
        number:          estimate.number,
        date:            estimate.date           || null,
        client:          estimate.client         || null,
        address:         estimate.address        || null,
        address_details: estimate.addressDetails || {},
        subtitle:        estimate.subtitle       || null,
        valid_days:      estimate.validDays      || 30,
        notes:           estimate.notes          || null,
        lines:            serializeLines(estimate.lines || []),
        payment_schedule: estimate.paymentSchedule?.length > 0 ? estimate.paymentSchedule : null,
        total:            Math.round(total * 100) / 100,
        status:          estimate.status         || 'draft',
        created_by:      userId,
        updated_at:      new Date().toISOString(),
    }

    if (estimate.id) {
        const { data, error } = await supabase
            .from('estimates')
            .update(payload)
            .eq('id', estimate.id)
            .select()
            .single()
        if (error) throw new Error(error.message)
        await clearReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => {})
        return data
    }

    const { data, error } = await supabase
        .from('estimates')
        .insert(payload)
        .select()
        .single()
    if (error) throw new Error(error.message)
    await clearReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => {})
    return data
}

/**
 * Merge-only update of the address_details JSONB (e.g. contact backfill).
 * Does not bump updated_at so list ordering stays intact.
 */
export async function updateAddressDetails(id, addressDetails) {
    const { error } = await supabase
        .from('estimates')
        .update({ address_details: addressDetails })
        .eq('id', id)
    if (error) throw new Error(error.message)
    await clearReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => {})
    await clearReferenceCache(CACHE_KEY_ARCHIVED, null).catch(() => {})
}

/**
 * Flip the status of an estimate (e.g. draft ↔ sent) without touching other fields.
 */
export async function markEstimateStatus(id, status) {
    const { error } = await supabase
        .from('estimates')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
    if (error) throw new Error(error.message)
    await clearReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => {})
    await clearReferenceCache(CACHE_KEY_ARCHIVED, null).catch(() => {})
}

/**
 * Soft-delete an estimate by setting status to 'archived'.
 */
export async function archiveEstimate(id, userId, userEmail, userName) {
    const { error } = await supabase
        .from('estimates')
        .update({
            status: 'archived',
            archived_by: userId,
            archived_by_email: userEmail,
            archived_by_name: userName,
            updated_at: new Date().toISOString()
        })
        .eq('id', id)
    if (error) throw new Error(error.message)
    await clearReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => {})
    await clearReferenceCache(CACHE_KEY_ARCHIVED, null).catch(() => {})
}

/**
 * Generate a fresh estimate number: EYYYYMMDD-{uid}
 */
export function newEstimateNumber() {
    const d = new Date()
    const yyyy = d.getFullYear()
    const mm   = String(d.getMonth() + 1).padStart(2, '0')
    const dd   = String(d.getDate()).padStart(2, '0')
    const uid  = Date.now().toString(36).toUpperCase()
    return `E${yyyy}${mm}${dd}-${uid}`
}

/**
 * Clone an existing estimate into a new DB record.
 * Fresh number, today's date, status reset to draft.
 */
export async function cloneEstimate(id, userId) {
    const { data: src, error: fetchErr } = await supabase
        .from('estimates')
        .select('*')
        .eq('id', id)
        .single()
    if (fetchErr) throw new Error(fetchErr.message)
    if (!src) throw new Error('Estimate not found')

    const total = (src.lines || []).reduce(
        (s, l) => s + (l.qty || 0) * (l.price || 0), 0
    )

    const payload = {
        number:          newEstimateNumber(),
        date:            new Date().toISOString().split('T')[0],
        client:          src.client          || null,
        address:         src.address         || null,
        address_details: src.address_details || {},
        subtitle:        src.subtitle        || null,
        valid_days:      src.valid_days      ?? 30,
        notes:           src.notes           || null,
        lines:           src.lines           || [],
        payment_schedule: src.payment_schedule || null,
        total:            Math.round(total * 100) / 100,
        status:          'draft',
        created_by:      userId,
    }

    const { data, error } = await supabase
        .from('estimates')
        .insert(payload)
        .select()
        .single()
    if (error) throw new Error(error.message)
    await clearReferenceCache(CACHE_KEY_ACTIVE, null).catch(() => {})
    return data
}
